import { Component } from '@theme/component';

/**
 * Drives the hero-product configurator: color, Front/Back thumbnail
 * selection, size, and quantity; matching color+size to a real Shopify
 * variant (id, price, availability) when one is configured; a simple
 * crossfade when the main image changes; and the native Size Guide
 * dialog. Cart submission itself is left to the plain native product form
 * (no fetch interception) — this component only ever changes what's on
 * screen and which variant id / quantity the form will submit.
 *
 * Reads two small JSON payloads rendered by the section:
 *   [data-cc-product-variants] — minimal per-variant data (id/available/
 *     option1/option2/price), present only when a real product is set.
 *   [data-cc-gallery-images] — { "Pink_Front": url, "Pink_Back": url, ... },
 *     always present (built from the real cropped, background-removed
 *     photography either way).
 *
 * @extends {Component}
 */
export class CurlyCubsProductPicker extends Component {
  /** @type {Array<{id: number, available: boolean, option1: string|null, option2: string|null, price: string}>} */
  #variants = [];

  /** @type {Record<string, string>} */
  #galleryImages = {};

  #selectedColor = 'Pink';
  #selectedView = 'Front';
  #selectedSize = null;
  #quantity = 1;
  #hasProduct = false;

  connectedCallback() {
    super.connectedCallback();

    this.#hasProduct = this.dataset.hasProduct === 'true';
    this.#variants = this.#readJson('[data-cc-product-variants]') ?? [];
    this.#galleryImages = this.#readJson('[data-cc-gallery-images]') ?? {};

    const optionGroups = [...this.querySelectorAll('[data-cc-option-group]')];
    const colorSelected = optionGroups[0]?.querySelector('[data-cc-option-value].is-selected');
    if (colorSelected) this.#selectedColor = colorSelected.dataset.ccOptionValue ?? this.#selectedColor;
    const sizeSelected = optionGroups[1]?.querySelector('[data-cc-option-value].is-selected');
    if (sizeSelected) this.#selectedSize = sizeSelected.dataset.ccOptionValue ?? null;

    this.addEventListener('click', this.#onClick);

    const dialog = this.querySelector('[data-cc-size-guide]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    }
  }

  /**
   * @param {string} selector
   * @returns {any}
   */
  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node?.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const guideTrigger = target.closest('[data-cc-size-guide-trigger]');
    if (guideTrigger) {
      event.preventDefault();
      const dialog = this.querySelector('[data-cc-size-guide]');
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      return;
    }

    const guideClose = target.closest('[data-cc-size-guide-close]');
    if (guideClose) {
      event.preventDefault();
      const dialog = this.querySelector('[data-cc-size-guide]');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    const qtyDecrease = target.closest('[data-cc-qty-decrease]');
    const qtyIncrease = target.closest('[data-cc-qty-increase]');
    if (qtyDecrease || qtyIncrease) {
      event.preventDefault();
      this.#quantity = Math.max(1, this.#quantity + (qtyIncrease ? 1 : -1));
      const input = this.querySelector('[data-cc-qty-input]');
      if (input instanceof HTMLInputElement) input.value = String(this.#quantity);
      return;
    }

    const thumb = target.closest('[data-cc-view-value]');
    if (thumb instanceof HTMLElement) {
      event.preventDefault();
      this.#selectedView = thumb.dataset.ccViewValue ?? this.#selectedView;
      for (const sibling of this.querySelectorAll('[data-cc-view-value]')) {
        sibling.classList.toggle('is-selected', sibling === thumb);
        sibling.setAttribute('aria-pressed', String(sibling === thumb));
      }
      this.#updateImage();
      return;
    }

    const optionControl = target.closest('[data-cc-option-value]');
    if (optionControl instanceof HTMLElement) {
      event.preventDefault();
      const group = optionControl.closest('[data-cc-option-group]');
      const groups = [...this.querySelectorAll('[data-cc-option-group]')];
      const index = groups.indexOf(group);
      if (index === -1) return;

      for (const sibling of group.querySelectorAll('[data-cc-option-value]')) {
        sibling.classList.toggle('is-selected', sibling === optionControl);
        sibling.setAttribute('aria-pressed', String(sibling === optionControl));
      }

      if (index === 0) {
        this.#selectedColor = optionControl.dataset.ccOptionValue ?? this.#selectedColor;
        this.#updateImage();
        this.#updateThumbnails();
      } else {
        this.#selectedSize = optionControl.dataset.ccOptionValue ?? null;
      }

      if (this.#hasProduct) this.#updateVariant();
    }
  };

  #updateImage() {
    const url = this.#galleryImages[`${this.#selectedColor}_${this.#selectedView}`];
    const image = this.querySelector('[data-cc-product-image]');
    if (!(image instanceof HTMLImageElement) || !url || image.src === url) return;

    image.style.opacity = '0';
    window.setTimeout(() => {
      image.src = url;
      image.style.opacity = '1';
    }, 200);
  }

  #updateThumbnails() {
    for (const view of ['Front', 'Back']) {
      const url = this.#galleryImages[`${this.#selectedColor}_${view}`];
      const thumb = this.querySelector(`[data-cc-thumb="${view}"]`);
      if (thumb instanceof HTMLImageElement && url) thumb.src = url;
    }
  }

  #updateVariant() {
    const match = this.#variants.find(
      (variant) =>
        variant.option1 === this.#selectedColor && (this.#selectedSize == null || variant.option2 === this.#selectedSize)
    );

    const input = this.querySelector('[data-cc-variant-input]');
    const priceEl = this.querySelector('[data-cc-product-price]');
    const button = this.querySelector('[data-cc-add-to-cart]');

    if (input instanceof HTMLInputElement && match) input.value = String(match.id);
    if (priceEl && match) priceEl.textContent = match.price;

    if (button instanceof HTMLButtonElement) {
      const available = Boolean(match?.available);
      button.disabled = !match || !available;
      button.textContent = !match ? 'Unavailable' : available ? 'Add to Cart' : 'Sold Out';
    }
  }
}

if (!customElements.get('curlycubs-product-picker')) {
  customElements.define('curlycubs-product-picker', CurlyCubsProductPicker);
}
